import moment from 'moment';

import { timeformat } from '#public/commons-global';

import { NullTime } from './bootstrap-datepicker';

export const DefaultFixedPeriod = '7';
export const TuttiFixedPeriod = 'tutti';
export const NullFixedPeriod = '--';

export type SelectedPeriodType = {
    from: string | null;
    to: string | null;
};

export type SelectedMomentType = {
    from: Date;
    to: Date;
};

export class SelectedPeriod implements SelectedPeriodType {
    from: string | null;
    to: string | null;
    constructor(period: SelectedPeriodType | null) {
        this.from = period?.from || null;
        this.to = period?.to || null;
    }
    toMoment(): SelectedMomentType {
        if(!this.from || !this.to) {
            return SelectedPeriod.nullMoment();
        }
        return SelectedPeriod.normalizeMoment(
            new timeformat(this.from).toMoment(),
            new timeformat(this.to).toMoment(),
        );
    }
    static fromMoment(period: SelectedMomentType | null) {
        if(!period?.from || !period?.to || (period.from === NullTime) || (period.to === NullTime)) {
            return new SelectedPeriod(null);
        }
        const from = moment(period.from);
        const to = moment(period.to);
        return new SelectedPeriod(SelectedPeriod.normalizeXml(from, to));
    }
    static nullMoment() {
        return {
            from: NullTime,
            to: NullTime,
        };
    }
    static normalize(from: moment.Moment, to: moment.Moment) {
        from.hour(0).minute(0).second(0).millisecond(0);
        to.hour(23).minute(59).second(59).millisecond(999);
    }
    static normalizeXml(from: moment.Moment, to: moment.Moment): SelectedPeriodType {
        this.normalize(from, to);
        return {
            from: new timeformat(from).toXml(),
            to: new timeformat(to).toXml(),
        };
    }
    static normalizeMoment(from: moment.Moment, to: moment.Moment): SelectedMomentType {
        this.normalize(from, to);
        return {
            from: from.toDate(),
            to: to.toDate(),
        };
    }
    static fromFixedPeriod(base: moment.Moment | Date | undefined, period: string | number, sign: number) {
        return this.fromMoment(this.fromFixedPeriod2(base, period, sign));
    }
    static fromFixedPeriod2(base: moment.Moment | Date | undefined, period: string | number, sign: number): SelectedMomentType {
        if(period === TuttiFixedPeriod) {
            return SelectedPeriod.nullMoment();
        }
        const _base = base ? moment(base) : moment();
        if(sign > 0) {
            return SelectedPeriod.normalizeMoment(
                _base,
                moment(_base).add(Number(period), 'day'),
            );
        }
        return SelectedPeriod.normalizeMoment(
            moment(_base).add(-Number(period), 'day'),
            _base,
        );
    }
    static movePickers(period: SelectedMomentType, sign: number) {
        let from = moment(period.from);
        let to = moment(period.to);
        const diff = to.diff(from, 'day');
        if(sign > 0) {
            from = to;
            to = moment(from).add(diff, 'day');
        } else {
            to = from;
            from = moment(to).add(-diff, 'day');
        }
        return SelectedPeriod.normalizeMoment(from, to);
    }
    static cloneObject(period: SelectedPeriodType | undefined): SelectedPeriod | undefined {
        return period ? new SelectedPeriod(period) : undefined;
    }
    static cloneRaw(period: SelectedPeriodType | undefined): SelectedPeriodType | undefined {
        return period ? { from: period.from, to: period.to } : undefined;
    }
    static compare(period1: SelectedPeriodType | undefined, period2: SelectedPeriodType | undefined): boolean {
        if(period1 === period2) {
            return true;
        }
        if(!period1 !== !period2) {
            return false;
        }
        return (period1?.from === period2?.from) && (period1?.to === period2?.to);
    }
}

export type FixedPeriodTableType = Record<string, { last: string; next: string }>;

export const DefaultFixedPeriodTable: FixedPeriodTableType = {
    '7': {
        last: 'Ultima settimana',
        next: 'Settimana',
    },
    '31': {
        last: 'Ultimo mese',
        next: 'Mese',
    },
    '93': {
        last: 'Ultimi 3 mesi',
        next: '3 mesi',
    },
    '365': {
        last: 'Ultimo anno',
        next: 'Anno',
    },
} as const;

export const InvoiceFixedPeriodTable: FixedPeriodTableType = {
    '7': {
        last: 'Ultima settimana',
        next: 'Settimana',
    },
    '12': {
        last: 'Ultimi 12 giorni',
        next: '12 giorni',
    },
    '31': {
        last: 'Ultimo mese',
        next: 'Mese',
    },
    '93': {
        last: 'Ultimi 3 mesi',
        next: '3 mesi',
    },
    '365': {
        last: 'Ultimo anno',
        next: 'Anno',
    },
} as const;

export const OneDayFixedPeriodTable: FixedPeriodTableType = {
    '1': {
        last: 'Ultimo giorno',
        next: 'Giorno',
    },
    ...DefaultFixedPeriodTable,
} as const;
